import { cache } from "react";
import api from "./api";

// ─── Type Definitions ────────────────────────────────────────────────

export interface MediaCoverage {
  id: string;
  heading: string;
  title: string;
  description: string;
  externalLink: string;
  createdDate: string;
  isActive: boolean;
  order: number;
  companyId: string;
  destinationId?: string;
}

interface MediaCoverageApiResponse {
  pageNumber: number;
  pageSize: number;
  count: number;
  totalPages: number;
  succeeded: boolean;
  data: MediaCoverage[];
}

// ─── Server-side Data Fetching ───────────────────────────────────────

export const fetchMediaCoverage = cache(
  async (
    page = 1,
    pageSize = 8,
    destinationId?: string,
  ): Promise<{
    items: MediaCoverage[];
    totalPages: number;
    count: number;
  }> => {
    const res = await api.get<MediaCoverageApiResponse>(
      "website/mediacoverage",
      {
        params: {
          PageNumber: page,
          PageSize: pageSize,
          ...(destinationId ? { DestinationId: destinationId } : {}),
        },
      },
    );

    const items = (res.data?.data ?? []).filter((m) => m.isActive);
    return {
      items,
      totalPages: res.data?.totalPages ?? 1,
      count: res.data?.count ?? 0,
    };
  },
);

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export async function fetchMediaCoverageClient(
  page = 1,
  pageSize = 8,
  destinationId?: string,
): Promise<{
  items: MediaCoverage[];
  totalPages: number;
  count: number;
}> {
  const params = new URLSearchParams({
    PageNumber: String(page),
    PageSize: String(pageSize),
    CompanyId: COMPANY_ID,
  });
  if (destinationId) params.set("DestinationId", destinationId);

  const res = await fetch(`${BASE_URL}/website/mediacoverage?${params}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN}`,
    },
  });
  const json = await res.json();
  const items: MediaCoverage[] = (json.data ?? []).filter(
    (m: MediaCoverage) => m.isActive,
  );
  return {
    items,
    totalPages: json.totalPages ?? 1,
    count: json.count ?? 0,
  };
}
